import { useEffect } from 'react';

interface UseKeyboardProps {
  onDelete?: () => void;
  onCopy?: () => void;
  onCut?: () => void;
  onPaste?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onSpaceDown?: () => void;
  onSpaceUp?: () => void;
  onShiftDown?: () => void;
  onShiftUp?: () => void;
  onEscape?: () => void;
  onToggleClipMask?: () => void;
  onArrowKey?: (dx: number, dy: number) => void;
  onToolChange?: (tool: 'select' | 'rectangle' | 'circle' | 'text') => void;
  onExportThumbnail?: () => void;
  onExportPNG?: () => void;
  onLoadProject?: () => void;
  onSaveProject?: () => void;
  onNewProject?: () => void;
  onToggleLayerLock?: () => void;
  onMoveLayerUp?: () => void;
  onMoveLayerDown?: () => void;
  onMoveLayerToFront?: () => void;
  onMoveLayerToBack?: () => void;
}

/**
 * 키보드 단축키 훅
 */
export function useKeyboard({
  onDelete,
  onCopy,
  onCut,
  onPaste,
  onUndo,
  onRedo,
  onSpaceDown,
  onSpaceUp,
  onShiftDown,
  onShiftUp,
  onEscape,
  onToggleClipMask,
  onArrowKey,
  onToolChange,
  onExportThumbnail,
  onExportPNG,
  onLoadProject,
  onSaveProject,
  onNewProject,
  onToggleLayerLock,
  onMoveLayerUp,
  onMoveLayerDown,
  onMoveLayerToFront,
  onMoveLayerToBack
}: UseKeyboardProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 입력 필드에 포커스 시 단축키 무시
      const tag = (e.target && (e.target as HTMLElement).tagName) ? (e.target as HTMLElement).tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (e.target as HTMLElement).isContentEditable) {
        return;
      }

      // 키 표준화 (영문 알파벳 대소문자 이슈 방지)
      const key = (typeof e.key === 'string' && e.key.length === 1) ? e.key.toLowerCase() : e.key;

      if (e.code === 'Space') {
        e.preventDefault();
        onSpaceDown?.();
      } else if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
        onShiftDown?.();
      } else if (key === 'Escape' || key === 'Esc' || e.key === 'Escape') {
        e.preventDefault();
        onEscape?.();
      } else if (key === 'Delete' || e.key === 'Delete' || key === 'Backspace' || e.key === 'Backspace') {
        e.preventDefault();
        onDelete?.();
      } else if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.altKey) {
        // Ctrl/Cmd + Shift + Alt 조합
        if (key === 's') {
          e.preventDefault();
          onExportThumbnail?.();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
        // Ctrl/Cmd + Shift 조합
        if (key === 's') {
          e.preventDefault();
          onExportPNG?.();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.altKey) {
        // Ctrl/Cmd + Alt 조합
        if (e.code === 'BracketRight') {
          e.preventDefault();
          onMoveLayerToFront?.();
        } else if (e.code === 'BracketLeft') {
          e.preventDefault();
          onMoveLayerToBack?.();
        } else if (key === 'g' || key === 'c') {
          e.preventDefault();
          onToggleClipMask?.();
        } else if (key === 'z') {
          e.preventDefault();
          onRedo?.();
        } else if (key === 'o') {
          e.preventDefault();
          onLoadProject?.();
        } else if (key === 't') {
          e.preventDefault();
          onSaveProject?.();
        } else if (key === 'n') {
          e.preventDefault();
          onNewProject?.();
        } else if (e.code === 'KeyL') {
          e.preventDefault();
          onToggleLayerLock?.();
        }
      } else if (e.ctrlKey || e.metaKey) {
        // 키 반복 방지 (키를 누르고 있으면 repeat이 true가 됨)
        if (e.repeat) return;

        if (e.code === 'BracketRight') {
          e.preventDefault();
          onMoveLayerUp?.();
          return;
        }
        if (e.code === 'BracketLeft') {
          e.preventDefault();
          onMoveLayerDown?.();
          return;
        }
        
        switch (key) {
          case 'c':
            e.preventDefault();
            onCopy?.();
            break;
          case 'x':
            e.preventDefault();
            onCut?.();
            break;
          case 'v':
            e.preventDefault();
            onPaste?.();
            break;
          case 'y':
            e.preventDefault();
            onRedo?.();
            break;
          case 'z':
            e.preventDefault();
            if (e.shiftKey) {
              onRedo?.();
            } else {
              onUndo?.();
            }
            break;
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        // 방향키로 객체 이동
        e.preventDefault();
        const moveDistance = e.shiftKey ? 10 : 1;
        let dx = 0;
        let dy = 0;
        
        if (e.key === 'ArrowUp') {
          dy = -moveDistance;
        } else if (e.key === 'ArrowDown') {
          dy = moveDistance;
        } else if (e.key === 'ArrowLeft') {
          dx = -moveDistance;
        } else if (e.key === 'ArrowRight') {
          dx = moveDistance;
        }
        
        if (dx !== 0 || dy !== 0) {
          onArrowKey?.(dx, dy);
        }
      } else if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        // 툴 단축키 (Ctrl/Cmd/Alt가 눌려있지 않을 때만)
        switch (key) {
          case 'v':
            e.preventDefault();
            onToolChange?.('select');
            break;
          case 'r':
            e.preventDefault();
            onToolChange?.('rectangle');
            break;
          case 'e':
            e.preventDefault();
            onToolChange?.('circle');
            break;
          case 't':
            e.preventDefault();
            onToolChange?.('text');
            break;
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        onSpaceUp?.();
      } else if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
        onShiftUp?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [onDelete, onCopy, onCut, onPaste, onUndo, onRedo, onSpaceDown, onSpaceUp, onShiftDown, onShiftUp, onEscape, onToggleClipMask, onArrowKey, onToolChange, onExportThumbnail, onExportPNG, onLoadProject, onSaveProject, onNewProject, onToggleLayerLock, onMoveLayerUp, onMoveLayerDown, onMoveLayerToFront, onMoveLayerToBack]);
}

